import { useEffect, useRef, useState } from "react";
import styles from "./FileExplorerWithPreview.module.css";
import type { FileExplorerPreviewItem } from "./types";

type FilePreviewProps = {
  item: FileExplorerPreviewItem;
  mode?: "compact" | "full";
  selected?: boolean;
};

export function FilePreview({
  item,
  mode = "compact",
  selected = false,
}: FilePreviewProps) {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [imageSrc, setImageSrc] = useState(item.imageSrc || "");
  const [imageState, setImageState] = useState<
    "loading" | "loaded" | "fallback"
  >(item.imageSrc || item.previewStatus === "pending" ? "loading" : "fallback");

  useEffect(() => {
    const nextImageSrc = item.imageSrc || "";
    setImageSrc(nextImageSrc);
    setImageState(
      nextImageSrc || item.previewStatus === "pending" ? "loading" : "fallback",
    );
  }, [item.imageSrc, item.previewStatus]);

  useEffect(() => {
    const image = imageRef.current;
    if (!imageSrc || !image || !image.complete) {
      return;
    }

    if (image.naturalWidth > 0) {
      setImageState("loaded");
      return;
    }

    setImageState("fallback");
    setImageSrc("");
  }, [imageSrc]);

  return (
    <>
      <div
        className={mode === "full" ? styles.slideThumbnail : styles.thumbnail}
      >
        {imageState === "loading" ? (
          <span className={styles.thumbnailShimmer} aria-hidden="true" />
        ) : null}
        {imageSrc ? (
          <img
            ref={imageRef}
            alt={item.imageAlt ?? ""}
            className={styles.thumbnailImage}
            draggable={false}
            src={imageSrc}
            onDragStart={(event) => event.preventDefault()}
            onError={() => {
              setImageState("fallback");
              setImageSrc("");
            }}
            onLoad={() => setImageState("loaded")}
          />
        ) : (
          <div className={styles.thumbnailFallback} aria-hidden="true" />
        )}
      </div>
      {mode === "compact" ? (
        <>
          <span className={styles.fileName}>{item.name}</span>
          {item.metaText ? (
            <span className={styles.fileMeta}>{item.metaText}</span>
          ) : null}
          <span className={styles.rowArrow} aria-hidden="true" />
        </>
      ) : null}
    </>
  );
}
